import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { Spark } from "@/components/Doodles";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink";

const ResetPassword = () => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const requestReset = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
            setMsg(data.message);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    const doReset = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) {
            setError("Those two passwords don't match. Try again.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setMsg("New password stamped in. Taking you to your card…");
            setTimeout(() => navigate("/account"), 1200);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-cream px-5 py-24">
            <Seo title="Reset Password" description="Reset your Panther City Clubhouse member password." />
            <FadeUp className="w-full max-w-md">
                <form onSubmit={token ? doReset : requestReset} data-testid="reset-password-form" className="border-2 border-ink bg-paper p-8 hard-shadow-lg -rotate-1">
                    <Spark className="h-8 w-8 text-orange" />
                    <h1 className="mt-2 font-display text-3xl tracking-wide text-ink">{token ? "PICK A NEW PASSWORD" : "FORGOT THE SECRET HANDSHAKE?"}</h1>
                    {msg ? (
                        <p data-testid="reset-success" className="mt-5 border-2 border-dashed border-forest bg-cream px-4 py-3 font-semibold text-forest">{msg}</p>
                    ) : token ? (
                        <>
                            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="reset-password">New password</label>
                            <input id="reset-password" data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="reset-confirm">Once more</label>
                            <input id="reset-confirm" data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="reset-submit-button" className="btn-stamp mt-6 w-full !bg-orange disabled:opacity-60">
                                {busy ? "Stamping…" : "Reset Password"}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="mt-3 text-sm text-ink/70">Give us the email on your account and we'll send a reset link. The link lives for one hour.</p>
                            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="reset-email">Email</label>
                            <input id="reset-email" data-testid="reset-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="reset-request-button" className="btn-stamp mt-6 w-full !bg-orange disabled:opacity-60">
                                {busy ? "Sending…" : "Send Reset Link"}
                            </button>
                        </>
                    )}
                    {error && <p data-testid="reset-error" className="mt-3 font-semibold text-brick">{error}</p>}
                    <p className="mt-4 text-sm text-ink/60">
                        Remembered it after all? <Link to="/account" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-4">Back to sign in</Link>
                    </p>
                </form>
            </FadeUp>
        </div>
    );
};

export default ResetPassword;
